import { CaretDown, CaretUp, Market } from '@creditornot/cb-icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Popover } from '@creditornot/cb-popover';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography } from 'common/typography';

import { EntitySelectorOverlay } from './EntitySelectorOverlay';
import { useExperienceModuleContext } from '../../../context';
import { localizedEntityName } from '../../../utils';

const SelectedContainer = styled.div`
  min-width: 250px;
  display: flex;
  height: 36px;
  padding: 6px 12px;

  justify-content: space-between;
  align-items: center;

  border-radius: 8px;
  border: 1px solid ${color.border};
  background: ${color.bg};
  color: ${color.text};
  fill: ${color.text};
  cursor: pointer;
  user-select: none;

  &:hover {
    color: ${color.textHovered};
    fill: ${color.textHovered};
    background: ${color.bgSurfaceHovered};
  }

  &:active {
    color: ${color.textPressed};
    fill: ${color.textPressed};
    background: ${color.bgSurfacePressed};
  }
`;

const StyledMarket = styled(Market)`
  width: 16px;
  height: 16px;
`;

const SelectedEntityTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SelectedEntityTitleText = styled(Typography.Body3)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const StyledCaretDown = styled(CaretDown)`
  width: 12px;
  height: 12px;
`;

const StyledCaretUp = styled(CaretUp)`
  width: 12px;
  height: 12px;
`;

export const EntitySelector = ({ isVenueSpecific }: { isVenueSpecific?: boolean }) => {
  const { experienceEntity, selectedExperienceEntity, selectedExperienceVenueEntity } = useExperienceModuleContext();
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const { locale } = useIntl();
  const isBusinessPageAndTheSwitcherEntitySelected =
    !isVenueSpecific && experienceEntity.experienceEntityId === selectedExperienceEntity.experienceEntityId;

  return (
    <Popover
      content={
        <EntitySelectorOverlay isVenueSpecific={isVenueSpecific} handleEntityChange={() => setIsSelectorOpen(false)} />
      }
      placement="bottom"
      open={isSelectorOpen}
      onCloseStart={() => setIsSelectorOpen(false)}
    >
      {({ refProps }) => (
        <SelectedContainer
          {...refProps}
          onClick={e => {
            setIsSelectorOpen(!isSelectorOpen);
            refProps.onClick?.(e);
          }}
        >
          <SelectedEntityTitle>
            <StyledMarket />
            <SelectedEntityTitleText>
              {isBusinessPageAndTheSwitcherEntitySelected ? (
                <FormattedMessage id="experience.search.filter.all" />
              ) : (
                localizedEntityName(
                  isVenueSpecific && selectedExperienceVenueEntity
                    ? selectedExperienceVenueEntity
                    : selectedExperienceEntity,
                  locale,
                )
              )}
            </SelectedEntityTitleText>
          </SelectedEntityTitle>
          {isSelectorOpen ? <StyledCaretUp /> : <StyledCaretDown />}
        </SelectedContainer>
      )}
    </Popover>
  );
};
